import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  image: string;
  date: string;
  readTime: string;
}

interface BlogPostRow extends BlogPost {
  title_vi: string | null;
  excerpt_vi: string | null;
  category_vi: string | null;
  read_time_vi: string | null;
}

export function useBlogPosts() {
  const [rows, setRows] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchPosts() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_blog_posts')
          .select('id, slug, title, title_vi, excerpt, excerpt_vi, category, category_vi, image, date, read_time, read_time_vi')
          .order('id', { ascending: true });

        if (err) throw err;
        if (!cancelled) setRows((data ?? []) as BlogPostRow[]);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load blog posts');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const posts: BlogPost[] = rows.map((r) => ({
    id: r.id,
    slug: r.slug,
    title: loc(language, r.title, r.title_vi),
    excerpt: loc(language, r.excerpt ?? '', r.excerpt_vi),
    category: loc(language, r.category ?? '', r.category_vi),
    image: r.image ?? '',
    date: r.date ?? '',
    readTime: loc(language, r.read_time ?? '', r.read_time_vi),
  }));

  return { posts, loading, error, refresh };
}